/**
 * Aggregation functions for capacity calculations
 *
 * These functions group and aggregate raw data by provider and date.
 * All calculations are pure functions with no side effects.
 */

import { getDateKey, calculateMinutes, isDateInRange } from '../utils/dateHelpers';
import {
  ShiftData,
  VisitData,
  CalendarBlockData,
  ProviderCapacityDay,
  RoleCapacityDay,
} from '../types/capacity.types';

/**
 * Aggregate shift minutes per provider per day
 */
export function aggregateShiftMinutes(
  shifts: ShiftData[],
  providerId: number,
  dateKey: string
): number {
  return shifts
    .filter((shift) => {
      const shiftDate = getDateKey(shift.startTime);
      return shift.providerId === providerId && shiftDate === dateKey;
    })
    .reduce((total, shift) => {
      return total + calculateMinutes(shift.startTime, shift.endTime);
    }, 0);
}

/**
 * Aggregate visit minutes per provider per day
 */
export function aggregateVisitMinutes(
  visits: VisitData[],
  providerId: number,
  dateKey: string
): number {
  let totalMinutes = 0;

  visits.forEach((visit) => {
    visit.visitResources
      .filter((resource) => {
        const resourceDate = getDateKey(resource.startTime);
        return resource.providerId === providerId && resourceDate === dateKey;
      })
      .forEach((resource) => {
        totalMinutes += calculateMinutes(resource.startTime, resource.endTime);
      });
  });

  return totalMinutes;
}

/**
 * Aggregate break minutes per provider per day
 */
export function aggregateBreakMinutes(
  calendarBlocks: CalendarBlockData[],
  providerId: number,
  dateKey: string
): number {
  return calendarBlocks
    .filter((block) => {
      const blockDate = getDateKey(block.startTime);
      return block.providerId === providerId && blockDate === dateKey;
    })
    .reduce((total, block) => {
      return total + calculateMinutes(block.startTime, block.endTime);
    }, 0);
}

/**
 * Group providers by ID for quick lookup
 */
export function groupProvidersById<T extends { providerId: number }>(
  items: T[]
): Map<number, T[]> {
  const grouped = new Map<number, T[]>();

  items.forEach((item) => {
    const existing = grouped.get(item.providerId) || [];
    grouped.set(item.providerId, [...existing, item]);
  });

  return grouped;
}

/**
 * Get unique provider IDs from multiple data sources
 */
export function getUniqueProviderIds(
  shifts: ShiftData[],
  visits: VisitData[],
  calendarBlocks: CalendarBlockData[]
): number[] {
  const providerIds = new Set<number>();

  shifts.forEach((shift) => providerIds.add(shift.providerId));

  visits.forEach((visit) => {
    visit.visitResources.forEach((resource) => {
      providerIds.add(resource.providerId);
    });
  });

  calendarBlocks.forEach((block) => providerIds.add(block.providerId));

  return Array.from(providerIds);
}

/**
 * Aggregate capacity data by role
 */
export function aggregateByRole(
  providerCapacityDays: ProviderCapacityDay[]
): RoleCapacityDay[] {
  // Group by role and date
  const grouped = new Map<string, ProviderCapacityDay[]>();

  providerCapacityDays.forEach((day) => {
    const key = `${day.role}|${day.date}`;
    const existing = grouped.get(key) || [];
    grouped.set(key, [...existing, day]);
  });

  // Aggregate each group
  const roleCapacityDays: RoleCapacityDay[] = [];

  grouped.forEach((days, key) => {
    const [role, date] = key.split('|');

    const totalShiftMinutes = days.reduce((sum, day) => sum + day.shiftMinutes, 0);
    const totalVisitMinutes = days.reduce((sum, day) => sum + day.visitMinutes, 0);
    const gapMinutes = totalShiftMinutes - totalVisitMinutes;
    const surplusPercent = totalShiftMinutes > 0
      ? (gapMinutes / totalShiftMinutes) * 100
      : 0;

    roleCapacityDays.push({
      role,
      date,
      totalShiftMinutes,
      totalVisitMinutes,
      gapMinutes,
      surplusPercent,
      providerCount: days.length,
    });
  });

  return roleCapacityDays;
}
